import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { VmeInboxJobsService } from '../../services';
import { IFacetedFilterSavedSearch } from '../../interfaces/faceted-filter-saved-search';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { every } from 'lodash';
import { Message } from '../../classes/Message';
import { MessageService } from 'primeng';
import { NoEmptyString } from '../../interfaces/no-empty-string.validator';

@Component({
  selector: 'app-faceted-filtering-saved-searches',
  templateUrl: './faceted-filtering-saved-searches.component.html',
  styleUrls: ['./faceted-filtering-saved-searches.component.scss'],
})
export class FacetedFilteringSavedSearchesComponent implements OnInit {
  @Input() selectedFilters;
  @Input() definitions;
  @Output() resetFilters = new EventEmitter();
  @Output() setSelection = new EventEmitter<any>();

  constructor(
    private vmeInboxjobsService: VmeInboxJobsService,
    private formBuilder: UntypedFormBuilder,
    private confirmation: ConfirmationService,
    private messageService: MessageService,
  ) {}

  displaySaveDialog: boolean = false;
  saveSearchForm: UntypedFormGroup;

  // the loaded search data selected via dropdown
  loadedSearch: IFacetedFilterSavedSearch;

  savedSearches: IFacetedFilterSavedSearch[];
  savedSearchesOptions; // for dropdown
  selectedSearch;

  // set to true when a global search is loaded (prevent global search being made local)
  globalLocked: boolean = false;
  isNewSearch: boolean = false; // set when user modifies save name on existing search

  ngOnInit() {
    this.saveSearchForm = this.formBuilder.group({
      searchName: new UntypedFormControl(
        this.loadedSearch ? this.loadedSearch.searchName : '',
        Validators.compose([Validators.required, Validators.maxLength(128), NoEmptyString]),
      ),
      isGlobal: new UntypedFormControl(false),
    });

    this.saveSearchForm.get('searchName').valueChanges.subscribe((x) => {
      let existing = this.savedSearchesOptions.find((y) => y.labelValue == x);
      this.isNewSearch =
        !existing &&
        ((this.loadedSearch && x != this.loadedSearch.searchName) || !this.loadedSearch);
    });

    // load saved searches to populate dropdown
    this.vmeInboxjobsService.getSavedFacetedSearches().then((result) => {
      this.savedSearches = result;

      this.savedSearchesOptions = [{ label: '(None)', labelValue: '(None)', value: null }];
      this.savedSearches.forEach((x) => {
        // this.savedSearchesOptions.push({ label: x.searchName, value: x.id })
        this.savedSearchesOptions.push({
          label: x.isGlobal ? `fa fa-globe fa-lg` : 'fa fa-user fa-lg',
          labelValue: x.searchName,
          value: x.id,
          isGlobal: x.isGlobal,
        });
      });
    });
  }

  selectSearch(event: any) {
    // load the selected search
    //console.log("Selected search: ", event, " - ",  this.selectedSearch);
    if (!event.value) {
      // selecting (none) we need to check if the current filter has changed
      // parse selected filters from loaded search into an array before stringifying, this prevents escape chars being in the json string
      let selected =
        this.loadedSearch && this.loadedSearch.selectedFilters
          ? (JSON.parse(this.loadedSearch.selectedFilters) as string[])
          : this.selectedFilters;
      if (!this.searchesAreTheSame(selected, this.selectedFilters)) {
        this.confirmChangeFilters(null, true);
      } else {
        // clear filters in main component
        this.resetFilters.emit();
      }

      return;
    }

    this.vmeInboxjobsService.getFacetedSearch(event.value).then((result) => {
      //console.log("Loaded filter: ", result);

      // if there are no selected filters, or the loaded search is not different from the current selection, just load the search
      let selected =
        this.loadedSearch && this.loadedSearch.selectedFilters
          ? (JSON.parse(this.loadedSearch.selectedFilters) as string[])
          : this.selectedFilters;
      let searchIsModified = !this.searchesAreTheSame(selected, this.selectedFilters);

      if (every(this.selectedFilters, (x) => x == '') || !searchIsModified) {
        this.setLoadedSearch(result);
        //console.log("SET Loaded filter");
        return;
      }

      //console.log("Comparing current: selected: ", this.selectedFilters, "   (selection): ", selection, "  ==  (current): ", current);
      if (
        !this.searchesAreTheSame(result.selectedFilters, this.selectedFilters) ||
        searchIsModified
      ) {
        //console.log("CONFIRMING");
        this.confirmChangeFilters(result);
      } else {
        this.setLoadedSearch(result);
        //console.log("SET Loaded filter B");
      }
    });
  }

  searchesAreTheSame(a: any, b: any): boolean {
    let selection = JSON.stringify(a);
    let current = JSON.stringify(b);

    return selection == current;
  }

  confirmChangeFilters(loadedSearch, doClear = false) {
    this.confirmation.confirm({
      message: 'Loading this filter will override current settings, do you want to continue?',
      accept: () => {
        // raise clear filters event
        if (doClear) {
          this.resetFilters.emit();
        } else {
          this.setLoadedSearch(loadedSearch);
        }
      },
      reject: () => {
        // change selected search back to previous
        this.selectedSearch = this.loadedSearch ? this.loadedSearch.id : null;
      },
    });
  }

  setLoadedSearch(loadedSearch) {
    this.loadedSearch = loadedSearch;
    this.globalLocked = this.loadedSearch.isGlobal;
    //console.log("Global locked set to: ", this.globalLocked);

    // raise event so selected filters are set to current loaded search
    // convert selection to json object
    this.setSelection.emit(JSON.parse(this.loadedSearch.selectedFilters));
  }

  saveSearchDialog() {
    // set form control values to currently loaded search (if any)
    if (this.loadedSearch) {
      this.saveSearchForm.controls['searchName'].setValue(this.loadedSearch.searchName);
      this.saveSearchForm.controls['isGlobal'].setValue(this.loadedSearch.isGlobal);
    }

    // open the modal dialog to allow user to save search as new or existing
    this.displaySaveDialog = true;
    //console.log("Display dialog set to true");
  }

  saveSearch() {
    if (!this.definitions) {
      this.messageService.add(
        Message.Error('Cannot save search, definitions not supplied', 'Error'),
      );
      return;
    }

    // construct faceted search object using selected filters and form values
    let filters = [];
    this.selectedFilters.forEach((filter, index) => {
      if (filter && filter.length > 0) {
        filters.push({
          definitionName: this.definitions[index].propertyName,
          values: filter.map((x) => x.value),
        });
      }
    });

    let searchDTO = <IFacetedFilterSavedSearch>{
      ...this.saveSearchForm.value,
      selectedFilters: JSON.stringify(filters),
    };

    //console.log("Saving search: ", searchDTO);

    this.vmeInboxjobsService.saveFacetedSearch(searchDTO).then((result) => {
      // set loaded search values to the current selection (prevents change warning dialog)
      if (this.loadedSearch) {
        this.loadedSearch = result;
      }

      this.displaySaveDialog = false;
      this.messageService.add(Message.Success('Search filter(s) saved', 'Search Saved'));

      //console.log("Save result: ", result);

      // add filter to dropdown list if not existing
      let exists = this.savedSearchesOptions.find((x) => x.labelValue == result.searchName);

      if (!exists) {
        this.savedSearchesOptions.push({
          label: result.isGlobal ? `fa fa-globe fa-lg` : 'fa fa-user fa-lg',
          labelValue: result.searchName,
          value: result.id,
          isGlobal: result.isGlobal,
        });
      }
    });

    this.displaySaveDialog = false;
  }

  closeDialog() {
    this.displaySaveDialog = false;
  }

  noFilters() {
    return (
      !this.selectedFilters ||
      this.selectedFilters.length == 0 ||
      every(this.selectedFilters, (x) => x == '')
    );
  }

  deleteSearch() {
    this.confirmation.confirm({
      message: 'Are you sure you want to delete this search?',
      accept: () => {
        // delete the search
        this.vmeInboxjobsService.deleteFacetedSearch(this.loadedSearch.id).then((success) => {
          // remove from dropdown list
          if (success) {
            this.messageService.add(Message.Success('Search filter deleted', 'Search Deleted'));

            this.savedSearchesOptions = this.savedSearchesOptions.filter(
              (x) => x.labelValue != this.loadedSearch.searchName,
            );

            // return to (none) search
            this.loadedSearch = null;
            this.resetFilters.emit();
          }
        });
      },
    });
  }
}
